import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useTutorial } from '../contexts/TutorialContext';
import { makeAuthenticatedRequest, handleApiError } from '../utils/api';
import HexGrid from './HexGrid';
import TownDetailsModal from './TownDetailsModal';
import './HexWorldMap.css';

interface HexTile {
  id: number;
  x: number;
  y: number;
  tileType: string;
  biomeType: string;
  zoneLevel: number;
  name: string;
  description: string;
  requiredLevel: number;
  isStartingTile: boolean;
  isGateTile: boolean;
  allowsGathering: boolean;
  gatheringType?: string;
  allowsBattle: boolean;
  maxPlayerUnits: number;
  imageUrl?: string;
  iconName?: string;
  hexOffsetRow: number;
  isUnlocked: boolean;
  battlesWon: number;
  isCurrentLocation: boolean;
  hasResources: boolean;
  hasMonsters: boolean;
  // Town properties for boss battles
  townId?: number;
  townName?: string;
  bossName?: string;
  townType?: string;
}

interface TileDetails {
  tile: {
    id: number;
    name: string;
    description: string;
    tileType: string;
    biomeType: string;
    zoneLevel: number;
    isGateTile: boolean;
    allowsGathering: boolean;
    allowsBattle: boolean;
    maxPlayerUnits: number;
  };
  resources: Array<{
    id: number;
    resourceType: string;
    currentStock: number;
    maxStock: number;
    resourceRarity: number;
    rarityName: string;
    respawnTime: number;
    isAvailable: boolean;
  }>;
  monsters: Array<{
    id: number;
    name: string;
    level: number;
    type: string;
    imageUrl?: string;
    difficultyLevel: number;
    spawnSlot: number;
    respawnTime: number;
    isAvailable: boolean;
    lastDefeatedAt?: string;
    minutesUntilRespawn?: number;
  }>;
  totalMonsterSpawns: number;
  totalResourceNodes: number;
}

interface HexWorldMapProps {
  onStartBattle?: (battleId: number) => void;
}

interface Character {
  id: number;
  name: string;
  level: number;
  class: string;
  currentHP: number;
  maxHP: number;
}

interface Town {
  id: number;
  name: string;
  description: string;
  townType: string;
  worldX: number;
  worldY: number;
  bossName: string;
  isLiberated: boolean;
  standing: number;
  level: number;
  lastActivityDate: string | null;
  canFastTravel: boolean;
}

interface ActiveGathering {
  id: number;
  characterId: number;
  characterName: string;
  tileId: number;
  tileName: string;
  gatheringType: string;
  startTime: string;
  totalCollections: number;
  elapsedTime: string;
  pendingCollections: number;
}

const HexWorldMap: React.FC<HexWorldMapProps> = ({ onStartBattle }) => {
  const { handleAuthError } = useAuth();
  const { addNotification } = useNotifications();
  const { checkAndStartTutorial } = useTutorial();
  const [tiles, setTiles] = useState<HexTile[]>([]);
  const [currentTileId, setCurrentTileId] = useState<number>(0);
  const [travelPoints, setTravelPoints] = useState<number>(3);
  const [selectedTile, setSelectedTile] = useState<HexTile | null>(null);
  const [tileDetails, setTileDetails] = useState<TileDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(2); // Start at 200% zoom
  const [message, setMessage] = useState<string>('');
  const [viewRadius, setViewRadius] = useState<number>(10); // Show tiles within 10 units of player
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [characters, setCharacters] = useState<Character[]>([]);
  const [selectedCharacterIds, setSelectedCharacterIds] = useState<number[]>([]);
  const [selectedMonsterSpawn, setSelectedMonsterSpawn] = useState<number | null>(null);

  // Town state
  const [towns, setTowns] = useState<Town[]>([]);
  const [selectedTownId, setSelectedTownId] = useState<number | null>(null);

  // Gathering state
  const [activeGathering, setActiveGathering] = useState<ActiveGathering[]>([]);
  const [selectedGatherCharacterId, setSelectedGatherCharacterId] = useState<number | null>(null);

  const fetchWorldMap = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await makeAuthenticatedRequest('/worldmap/tiles');

      if (response.ok) {
        const data = await response.json();
        console.log('[HEX TP DEBUG] TP Data:', {
          travelPoints: data.travelPoints,
          lastTpRegeneration: data.lastTpRegeneration,
          currentTime: new Date().toISOString()
        });
        setTiles(data.tiles || []);
        setCurrentTileId(data.currentTileId);
        setTravelPoints(data.travelPoints);

        if (data.message) {
          setMessage(data.message);
        }
      } else {
        setError('Failed to fetch world map');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  }, [handleAuthError]);

  const fetchCharacters = useCallback(async () => {
    try {
      const response = await makeAuthenticatedRequest('/characters');
      if (response.ok) {
        const data = await response.json();
        setCharacters(data);
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
      }
    }
  }, [handleAuthError]);

  const fetchTowns = useCallback(async () => {
    try {
      const response = await makeAuthenticatedRequest('/town');
      if (response.ok) {
        const data = await response.json();
        setTowns(data);
      }
    } catch (err) {
      console.error('Error fetching towns:', err);
    }
  }, []);

  const fetchTileDetails = useCallback(async (tileId: number) => {
    try {
      setIsLoading(true);
      const response = await makeAuthenticatedRequest(`/worldmap/tiles/${tileId}/details`);

      if (response.ok) {
        const data = await response.json();
        setTileDetails(data);
      } else {
        setError('Failed to fetch tile details');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  }, [handleAuthError]);

  // Calculate hex distance using axial coordinates
  const calculateHexDistance = (tile1: HexTile | null, tile2: HexTile): number => {
    if (!tile1) return 0;
    // Convert axial to cube coordinates: q = x, r = y, s = -q - r
    const q1 = tile1.x, r1 = tile1.y, s1 = -q1 - r1;
    const q2 = tile2.x, r2 = tile2.y, s2 = -q2 - r2;
    // Hex distance in cube coordinates
    const distance = (Math.abs(q1 - q2) + Math.abs(r1 - r2) + Math.abs(s1 - s2)) / 2;
    return distance;
  };

  const handleTravel = async (tileId: number) => {
    // Check if any character is currently gathering
    if (activeGathering.length > 0) {
      const gatheringNames = activeGathering.map(g => g.characterName).join(', ');
      addNotification({
        type: 'info',
        title: '⚠️ Characters Gathering',
        message: `${gatheringNames} is currently gathering. Stop gathering before traveling.`
      });
      setError(`Stop gathering first: ${gatheringNames}`);
      return;
    }

    try {
      setIsLoading(true);
      const response = await makeAuthenticatedRequest(`/worldmap/travel/${tileId}`, {
        method: 'POST'
      });

      if (response.ok) {
        const data = await response.json();
        setTravelPoints(data.travelPoints);
        await fetchWorldMap(); // Refresh map
        setSelectedTile(null);
        setTileDetails(null);
        setMessage(`Traveled to ${data.newLocation.name}`);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to travel');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartBattle = async () => {
    if (!selectedTile || selectedCharacterIds.length === 0) {
      setError('Please select at least one character');
      return;
    }

    // Only require monster spawn selection for non-boss battles
    if (!selectedTile.bossName && selectedMonsterSpawn === null) {
      setError('Please select a monster spawn to battle');
      return;
    }

    if (!selectedTile.isCurrentLocation) {
      setError('You must travel to this tile first to start a battle');
      return;
    }

    try {
      setIsLoading(true);
      const response = await makeAuthenticatedRequest(`/worldmap/tiles/${selectedTile.id}/battle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          characterIds: selectedCharacterIds,
          monsterSpawnId: selectedTile.bossName ? null : selectedMonsterSpawn  // Send null for boss battles
        })
      });

      if (response.ok) {
        const data = await response.json();
        setMessage(`Battle started! ${data.playerUnits} vs ${data.enemyUnits} enemies`);

        // Call the onStartBattle callback if provided
        if (onStartBattle && data.battleId) {
          onStartBattle(data.battleId);
        }

        handleCloseModal();
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to start battle');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleCharacterSelection = (characterId: number) => {
    setSelectedCharacterIds(prev => {
      if (prev.includes(characterId)) {
        return prev.filter(id => id !== characterId);
      } else {
        // Respect max player units from tile
        if (tileDetails && prev.length >= tileDetails.tile.maxPlayerUnits) {
          setError(`Maximum ${tileDetails.tile.maxPlayerUnits} characters allowed`);
          return prev;
        }
        return [...prev, characterId];
      }
    });
  };

  const getTownAtTile = (x: number, y: number): Town | undefined => {
    return towns.find(t => t.worldX === x && t.worldY === y);
  };

  const getTownIcon = (townType: string): string => {
    switch (townType) {
      case 'Mining': return '⛏️';
      case 'Forest': return '🌲';
      case 'Trading': return '🏪';
      case 'Military': return '⚔️';
      case 'Magical': return '🔮';
      default: return '🏰';
    }
  };

  const handleTileClick = (tile: HexTile) => {
    // Check if this tile has a town
    const town = getTownAtTile(tile.x, tile.y);

    if (town && town.isLiberated) {
      // Liberated town - show town modal (has quests if you're there, fast travel if not)
      setSelectedTownId(town.id);
      setSelectedTile(null); // Don't show tile modal
      setTileDetails(null); // Clear tile details
      setError(null);
      setSelectedCharacterIds([]); // Reset selection when opening new tile
      setSelectedMonsterSpawn(null); // Reset monster spawn selection
    } else {
      // Regular tile OR un-liberated town - show tile modal
      setSelectedTownId(null); // Don't show town modal
      setSelectedTile(tile);
      setError(null);
      setSelectedCharacterIds([]); // Reset selection when opening new tile
      setSelectedMonsterSpawn(null); // Reset monster spawn selection
      fetchTileDetails(tile.id);
    }
  };

  const handleCloseModal = () => {
    setSelectedTile(null);
    setTileDetails(null);
    setSelectedCharacterIds([]);
    setSelectedMonsterSpawn(null);
    setSelectedGatherCharacterId(null);
    setSelectedTownId(null); // Also close town modal if open
  };

  const fetchActiveGathering = useCallback(async () => {
    try {
      const response = await makeAuthenticatedRequest('/gathering/active');
      if (response.ok) {
        const data = await response.json();
        setActiveGathering(data.activeGathering || []);
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
      }
      // Silently fail for gathering fetch - not critical
    }
  }, [handleAuthError]);

  const handleStartGathering = async () => {
    if (!selectedTile || !selectedGatherCharacterId) {
      setError('Please select a character to gather');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await makeAuthenticatedRequest(
        `/gathering/start/${selectedGatherCharacterId}/${selectedTile.id}`,
        { method: 'POST' }
      );

      if (response.ok) {
        const data = await response.json();
        setMessage(`${data.characterName} started gathering ${data.gatheringType} at ${data.tileName}`);
        await fetchActiveGathering();
        setSelectedGatherCharacterId(null);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to start gathering');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStopGathering = async (gatheringId: number) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await makeAuthenticatedRequest(
        `/gathering/stop/${gatheringId}`,
        { method: 'POST' }
      );

      if (response.ok) {
        const data = await response.json();

        // Build materials list
        const materialsText = data.materialsCollected && data.materialsCollected.length > 0
          ? data.materialsCollected.map((m: any) => `${m.quantity}x ${m.materialName}`).join(', ')
          : 'No materials';

        const bonusText = data.bonuses ? ` (${data.bonuses.gatheringBonus} town bonus!)` : '';
        const fullMessage = `${data.characterName} gathered: ${materialsText}${bonusText}`;

        setMessage(fullMessage);

        // Show success notification with details
        addNotification({
          type: 'loot',
          title: '✅ Gathering Complete!',
          message: fullMessage
        });

        await fetchActiveGathering();
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to stop gathering');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFastTravel = async (townId: number) => {
    try {
      setIsLoading(true);
      const response = await makeAuthenticatedRequest(`/town/${townId}/fast-travel`, {
        method: 'POST'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fast travel');
      }

      const data = await response.json();
      setMessage(`${data.message} (TP Cost: ${data.tpCost}, Remaining: ${data.remainingTp})`);

      // Refresh map and close town modal
      await fetchWorldMap();
      setSelectedTownId(null);
      setError(null);
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchWorldMap();
    fetchCharacters();
    fetchTowns();
    fetchActiveGathering();
  }, [fetchWorldMap, fetchCharacters, fetchTowns, fetchActiveGathering]);

  // Auto-trigger world map tutorial for first-time players
  useEffect(() => {
    if (tiles.length > 0 && !isLoading) {
      checkAndStartTutorial('world-map-basics');
    }
  }, [tiles, isLoading, checkAndStartTutorial]);

  // TP Regeneration polling - fetch every 30 seconds to trigger backend regeneration
  useEffect(() => {
    console.log('[HEX TP DEBUG] Setting up TP regeneration interval...');
    const tpInterval = setInterval(() => {
      console.log('[HEX TP DEBUG] 30-second TP regeneration check');
      fetchWorldMap(); // This calls /worldmap/tiles which triggers TP regeneration on backend
    }, 30000); // Every 30 seconds

    return () => {
      console.log('[HEX TP DEBUG] Cleaning up TP interval');
      clearInterval(tpInterval);
    };
  }, [fetchWorldMap]);

  // No longer need centerOnPlayer - HexGrid handles this via viewBox

  const getRarityColor = (rarity: number): string => {
    const colors = ['#9e9e9e', '#4caf50', '#2196f3', '#9c27b0', '#ff9800'];
    return colors[rarity - 1] || '#666';
  };

  // Filter tiles to only show those near the player's current location
  const getVisibleTiles = (): HexTile[] => {
    if (tiles.length === 0) return [];

    const currentTile = tiles.find(t => t.id === currentTileId);
    if (!currentTile) return tiles; // If can't find current tile, show all

    // Calculate distance from current tile and filter
    return tiles.filter(tile => {
      const dx = tile.x - currentTile.x;
      const dy = tile.y - currentTile.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      return distance <= viewRadius;
    });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsPanning(true);
    setPanStart({
      x: e.clientX - panOffset.x,
      y: e.clientY - panOffset.y
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isPanning) {
      // Calculate pan offset in screen pixels, will be converted to SVG units in HexGrid
      setPanOffset({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      // Scroll up - zoom in
      setZoom(prev => Math.min(3, prev + 0.5));
    } else {
      // Scroll down - zoom out
      setZoom(prev => Math.max(0.5, prev - 0.5));
    }
  };

  return (
    <div className="hex-world-map">
      <div className="hex-world-header">
        <div>
          <h2>🗺️ World Map</h2>
          <p className="world-description">Explore a vast procedural world filled with resources and monsters</p>
        </div>
        <div className="world-status">
          <div className="travel-points">
            <span className="tp-label">Travel Points:</span>
            <span className="tp-value">{travelPoints}/10</span>
          </div>

          {/* EMERGENCY STOP GATHERING BUTTON - Always visible if any gathering is active */}
          {activeGathering.length > 0 && (
            <div style={{ marginTop: '0.5rem' }}>
              <div style={{
                background: 'rgba(244, 67, 54, 0.2)',
                border: '2px solid #f44336',
                borderRadius: '6px',
                padding: '0.5rem'
              }}>
                <div style={{ color: '#f44336', fontSize: '0.85em', marginBottom: '0.25rem' }}>
                  ⚠️ Active Gathering ({activeGathering.length})
                </div>
                {activeGathering.map(g => (
                  <button
                    key={g.id}
                    onClick={() => handleStopGathering(g.id)}
                    disabled={isLoading}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      marginBottom: '0.25rem',
                      background: '#f44336',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.85em',
                      fontWeight: 'bold'
                    }}
                  >
                    Stop {g.characterName} @ {g.tileName}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {message && (
        <div className="info-message">
          {message}
          {message.includes('not generated') && (
            <div style={{ marginTop: '10px' }}>
              <small>The admin needs to generate the hex world first.</small>
            </div>
          )}
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {/* Hex Grid */}
      {tiles.length > 0 ? (
        <>
          <div
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onWheel={handleWheel}
            style={{
              overflow: 'hidden',
              position: 'relative',
              border: '2px solid #444',
              borderRadius: '8px',
              background: '#1a1a2e',
              height: '600px',
              cursor: isPanning ? 'grabbing' : 'grab'
            }}
          >
            <HexGrid
              tiles={getVisibleTiles()}
              currentTileId={currentTileId}
              onTileClick={handleTileClick}
              zoom={zoom}
              centerX={panOffset.x}
              centerY={panOffset.y}
              viewportWidth={1660}  // Actual container width
              viewportHeight={600}  // Actual container height
              towns={towns}
            />
          </div>

          {/* Map Controls - Now below the map */}
          <div className="map-controls" style={{ marginTop: '15px', display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
            <button style={{ width: 'auto', padding: '8px 16px' }} onClick={() => setZoom(Math.max(0.5, zoom - 0.5))} disabled={zoom <= 0.5}>
              🔍− Zoom Out
            </button>
            <span className="zoom-display" style={{ padding: '0 10px' }}>{Math.round(zoom * 100)}%</span>
            <button style={{ width: 'auto', padding: '8px 16px' }} onClick={() => setZoom(Math.min(3, zoom + 0.5))} disabled={zoom >= 3}>
              🔍+ Zoom In
            </button>
            <button style={{ width: 'auto', padding: '8px 16px' }} onClick={() => { setZoom(3); setPanOffset({ x: 0, y: 0 }); }}>
              🎯 Reset
            </button>
            <button style={{ width: 'auto', padding: '8px 16px' }} onClick={() => setViewRadius(viewRadius === 10 ? 999 : 10)} title={viewRadius === 10 ? 'Show full map' : 'Show nearby tiles only'}>
              {viewRadius === 10 ? '🗺️ Full Map' : '📍 Nearby'}
            </button>
            <span className="tile-count" style={{ fontSize: '0.85em', color: '#aaa', padding: '0 10px' }}>
              {getVisibleTiles().length} / {tiles.length} tiles
            </span>
            <button style={{ width: 'auto', padding: '8px 16px' }} onClick={fetchWorldMap} className="refresh-button">
              🔄 Refresh
            </button>
          </div>
        </>
      ) : (
        <div className="no-map-message">
          {isLoading ? 'Loading world map...' : 'No world map available'}
        </div>
      )}

      {/* Town Details Modal */}
      {selectedTownId && (
        <TownDetailsModal
          townId={selectedTownId}
          onClose={handleCloseModal}
          onFastTravel={handleFastTravel}
        />
      )}

      {/* Tile Details Modal */}
      {selectedTile && tileDetails && (
        <div className="tile-modal-overlay" onClick={handleCloseModal}>
          <div className="tile-modal hex-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>
                  {selectedTile.townName ? (
                    <>
                      🏰 {selectedTile.townName} ({selectedTile.x},{selectedTile.y})
                    </>
                  ) : (
                    selectedTile.name
                  )}
                </h3>
                <span className={`zone-badge zone-${selectedTile.zoneLevel}`}>
                  Zone {selectedTile.zoneLevel}
                </span>
                {selectedTile.townType && (
                  <span className={`zone-badge`} style={{ marginLeft: '8px', background: '#9c27b0' }}>
                    {selectedTile.townType}
                  </span>
                )}
              </div>
              <button className="close-button" onClick={handleCloseModal}>×</button>
            </div>

            <div className="modal-content">
              <p className="tile-description">{selectedTile.description}</p>

              <div className="tile-info-grid">
                <div className="info-item">
                  <span className="label">Biome:</span>
                  <span className="value">{selectedTile.biomeType}</span>
                </div>
                <div className="info-item">
                  <span className="label">Recommended Level:</span>
                  <span className="value">{selectedTile.requiredLevel}</span>
                </div>
                {!selectedTile.isUnlocked && (
                  <div className="info-item" style={{ gridColumn: '1 / -1', padding: '10px', background: 'rgba(244, 67, 54, 0.1)', border: '1px solid #f44336', borderRadius: '4px' }}>
                    <span style={{ color: '#f44336', fontWeight: 'bold' }}>🔒 Locked</span>
                    <span style={{ display: 'block', fontSize: '0.85em', marginTop: '5px' }}>
                      Win 3 battles on an adjacent tile to unlock
                    </span>
                  </div>
                )}
                {!selectedTile.bossName && selectedTile.isUnlocked && selectedTile.battlesWon < 3 && (
                  <div className="info-item" style={{ gridColumn: '1 / -1', padding: '10px', background: 'rgba(76, 175, 80, 0.1)', border: '1px solid #4caf50', borderRadius: '4px' }}>
                    <span style={{ color: '#4caf50', fontWeight: 'bold' }}>⚔️ Battle Progress</span>
                    <div style={{ marginTop: '5px' }}>
                      <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                        {[0, 1, 2].map(i => (
                          <div
                            key={i}
                            style={{
                              width: '30px',
                              height: '30px',
                              borderRadius: '4px',
                              background: i < selectedTile.battlesWon ? '#4caf50' : 'rgba(255,255,255,0.1)',
                              border: '1px solid ' + (i < selectedTile.battlesWon ? '#4caf50' : '#444'),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.85em'
                            }}
                          >
                            {i < selectedTile.battlesWon ? '✓' : (i + 1)}
                          </div>
                        ))}
                        <span style={{ fontSize: '0.85em', marginLeft: '10px' }}>
                          {selectedTile.battlesWon}/3 battles won
                        </span>
                      </div>
                      <small style={{ display: 'block', marginTop: '5px', fontSize: '0.8em', color: '#aaa' }}>
                        Win {3 - selectedTile.battlesWon} more battle{3 - selectedTile.battlesWon !== 1 ? 's' : ''} to unlock adjacent tiles
                      </small>
                    </div>
                  </div>
                )}
                {!selectedTile.bossName && selectedTile.battlesWon >= 3 && (
                  <div className="info-item" style={{ gridColumn: '1 / -1', padding: '8px', background: 'rgba(255, 193, 7, 0.1)', border: '1px solid #ffc107', borderRadius: '4px' }}>
                    <span style={{ color: '#ffc107', fontSize: '0.9em' }}>
                      ✨ Mastered ({selectedTile.battlesWon} battles won)
                    </span>
                  </div>
                )}
                {selectedTile.isGateTile && (
                  <div className="info-item gate-notice">
                    <span>🚪 Zone Gate - Boss Battle Required!</span>
                  </div>
                )}
              </div>

              {/* Resources Section */}
              {tileDetails.resources.length > 0 && (
                <div className="resources-section">
                  <h4>📦 Resources Available</h4>
                  <p style={{ fontSize: '0.85em', color: '#aaa', marginBottom: '0.5rem', fontStyle: 'italic' }}>
                    Resource nodes at this location. Start gathering to automatically collect materials over time.
                  </p>
                  <div className="resources-list">
                    {tileDetails.resources.map(resource => (
                      <div key={resource.id} className="resource-item">
                        <div className="resource-header">
                          <span className="resource-name">{resource.resourceType}</span>
                          <span
                            className="resource-rarity"
                            style={{ color: getRarityColor(resource.resourceRarity) }}
                          >
                            {resource.rarityName}
                          </span>
                        </div>
                        <div className="resource-stock">
                          <div className="stock-bar">
                            <div
                              className="stock-fill"
                              style={{
                                width: `${(resource.currentStock / resource.maxStock) * 100}%`,
                                background: getRarityColor(resource.resourceRarity)
                              }}
                            />
                          </div>
                          <span className="stock-text">
                            {resource.currentStock}/{resource.maxStock}
                          </span>
                        </div>
                        <small className="respawn-info">
                          Respawns every {resource.respawnTime} min
                        </small>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Gathering Section - ALWAYS show if gathering is active, even if allowsGathering is false */}
              {selectedTile.isCurrentLocation && (() => {
                const activeHere = activeGathering.find(g => g.tileId === selectedTile.id);
                return selectedTile.allowsGathering || activeHere; // Show if allows gathering OR if already gathering
              })() && (
                <div className="gathering-section" style={{
                  background: 'rgba(76, 175, 80, 0.1)',
                  border: '1px solid #4caf50',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginTop: '1rem'
                }}>
                  <h4 style={{ color: '#4caf50', marginBottom: '0.75rem' }}>
                    ⛏️ Gathering: {selectedTile.gatheringType}
                  </h4>

                  {/* Active gathering at this tile */}
                  {(() => {
                    const activeHere = activeGathering.find(g => g.tileId === selectedTile.id);
                    if (activeHere) {
                      return (
                        <div style={{
                          background: 'rgba(76, 175, 80, 0.2)',
                          padding: '1rem',
                          borderRadius: '6px',
                          marginBottom: '1rem'
                        }}>
                          <div style={{ marginBottom: '0.5rem' }}>
                            <strong style={{ color: '#4caf50' }}>
                              {activeHere.characterName} is gathering!
                            </strong>
                          </div>
                          <div style={{ fontSize: '0.85em', color: '#aaa', marginBottom: '0.5rem' }}>
                            Collections: {activeHere.totalCollections} |
                            Pending: {activeHere.pendingCollections} |
                            Time: {activeHere.elapsedTime}
                          </div>
                          <button
                            onClick={() => handleStopGathering(activeHere.id)}
                            disabled={isLoading}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              background: '#f44336',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontWeight: 'bold'
                            }}
                          >
                            Stop Gathering & Collect Materials
                          </button>
                        </div>
                      );
                    }

                    // No active gathering - show start option
                    const availableCharacters = characters.filter(c =>
                      !activeGathering.some(g => g.characterId === c.id) && c.currentHP > 0
                    );

                    if (availableCharacters.length === 0) {
                      return (
                        <p style={{ color: '#aaa', fontSize: '0.85em' }}>
                          No available characters. All characters are either gathering elsewhere or defeated.
                        </p>
                      );
                    }

                    return (
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9em' }}>
                          Select character to gather:
                        </label>
                        <select
                          value={selectedGatherCharacterId || ''}
                          onChange={(e) => setSelectedGatherCharacterId(Number(e.target.value))}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            background: '#16213e',
                            color: '#fff',
                            border: '1px solid #0f3460',
                            borderRadius: '4px',
                            marginBottom: '0.75rem'
                          }}
                        >
                          <option value="">-- Select Character --</option>
                          {availableCharacters.map(char => (
                            <option key={char.id} value={char.id}>
                              {char.name} (Lv.{char.level} {char.class})
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={handleStartGathering}
                          disabled={!selectedGatherCharacterId || isLoading}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            background: selectedGatherCharacterId ? '#4caf50' : '#555',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: selectedGatherCharacterId ? 'pointer' : 'not-allowed',
                            fontWeight: 'bold'
                          }}
                        >
                          {selectedGatherCharacterId ? '⛏️ Start Gathering' : 'Select a Character'}
                        </button>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Boss Battle Section (for towns) */}
              {selectedTile.bossName && (
                <div className="monsters-section">
                  <h4>👑 Boss Battle</h4>
                  <div style={{
                    padding: '16px',
                    background: 'linear-gradient(135deg, rgba(211, 47, 47, 0.2) 0%, rgba(156, 39, 176, 0.2) 100%)',
                    border: '2px solid #d32f2f',
                    borderRadius: '8px',
                    marginTop: '10px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                      <span style={{ fontSize: '2em' }}>👑</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#ff5252', fontWeight: 'bold', fontSize: '1.2em', marginBottom: '4px' }}>
                          {selectedTile.bossName}
                        </div>
                        <div style={{ color: '#aaa', fontSize: '0.9em' }}>
                          Defeat the boss to liberate this town!
                        </div>
                      </div>
                    </div>
                    <div style={{
                      padding: '12px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 82, 82, 0.3)'
                    }}>
                      <div style={{ color: '#ffc107', fontSize: '0.9em', marginBottom: '8px', fontWeight: 'bold' }}>
                        ⚠️ Boss Battle Features:
                      </div>
                      <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.85em', color: '#ccc' }}>
                        <li>Larger battle arena (20x15)</li>
                        <li>Boss has enhanced stats (+50% HP, +2 AP)</li>
                        <li>5 enemies total (boss + 4 minions)</li>
                        <li>💎 Double material rewards on victory!</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Monsters Section (for non-town tiles) */}
              {!selectedTile.bossName && tileDetails.monsters.length > 0 && (
                <div className="monsters-section">
                  <h4>⚔️ Monsters</h4>
                  {selectedTile.isCurrentLocation && (
                    <p style={{ fontSize: '0.85em', color: '#aaa', marginBottom: '10px' }}>
                      Click an available monster spawn to select it for battle
                    </p>
                  )}
                  <div className="monsters-list">
                    {tileDetails.monsters.map((monster, idx) => (
                      <div
                        key={`${monster.id}-${idx}`}
                        className="monster-item"
                        onClick={() => selectedTile.isCurrentLocation && monster.isAvailable && setSelectedMonsterSpawn(monster.id)}
                        style={{
                          cursor: selectedTile.isCurrentLocation && monster.isAvailable ? 'pointer' : 'default',
                          border: selectedMonsterSpawn === monster.id ? '2px solid #f44336' : undefined,
                          background: selectedMonsterSpawn === monster.id ? 'rgba(244, 67, 54, 0.1)' : undefined,
                          opacity: monster.isAvailable ? 1 : 0.5,
                          transition: 'all 0.2s'
                        }}
                      >
                        <div className="monster-info">
                          <span className="monster-name">
                            {selectedMonsterSpawn === monster.id && '⚔️ '}
                            <strong>Spawn {monster.spawnSlot}:</strong> {monster.name} (Lv.{monster.level})
                          </span>
                        </div>
                        <div className="monster-details">
                          <span className="monster-type" style={{
                            color: monster.isAvailable ? '#4caf50' : '#f44336',
                            fontWeight: 'bold'
                          }}>
                            {monster.isAvailable
                              ? '✓ Available'
                              : `Defeated - Respawns in ${monster.minutesUntilRespawn} min`}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Character Selection for Battle */}
              {selectedTile.isCurrentLocation && (tileDetails.monsters.length > 0 || selectedTile.bossName) && (
                <div className="battle-setup-section battle-character-section">
                  <h4>⚔️ Select Characters for Battle (Max: {tileDetails.tile.maxPlayerUnits})</h4>
                  {characters.length === 0 ? (
                    <p style={{ color: '#aaa', fontSize: '0.9em' }}>No characters available. Create a character first!</p>
                  ) : (
                    <div className="character-selection-list">
                      {characters.map(character => (
                        <div
                          key={character.id}
                          className={`character-select-item ${selectedCharacterIds.includes(character.id) ? 'selected' : ''}`}
                          onClick={() => toggleCharacterSelection(character.id)}
                          style={{
                            padding: '10px',
                            margin: '5px 0',
                            border: selectedCharacterIds.includes(character.id) ? '2px solid #4caf50' : '2px solid #444',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            background: selectedCharacterIds.includes(character.id) ? 'rgba(76, 175, 80, 0.1)' : 'rgba(0,0,0,0.2)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            transition: 'all 0.2s'
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: 'bold', color: '#fff' }}>
                              {selectedCharacterIds.includes(character.id) && '✓ '}
                              {character.name}
                            </div>
                            <div style={{ fontSize: '0.85em', color: '#aaa' }}>
                              Lv.{character.level} {character.class} | HP: {character.currentHP}/{character.maxHP}
                            </div>
                          </div>
                          {character.currentHP === 0 && (
                            <span style={{ color: '#f44336', fontSize: '0.85em' }}>Defeated</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ marginTop: '10px', fontSize: '0.85em', color: '#aaa' }}>
                    {selectedCharacterIds.length} / {tileDetails.tile.maxPlayerUnits} characters selected
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="modal-actions">
                {!selectedTile.isCurrentLocation && selectedTile.isUnlocked && (() => {
                  const currentTile = tiles.find(t => t.isCurrentLocation);
                  const tpCost = calculateHexDistance(currentTile || null, selectedTile);
                  const hasEnoughTP = travelPoints >= tpCost;

                  return (
                    <button
                      onClick={() => handleTravel(selectedTile.id)}
                      disabled={!hasEnoughTP || isLoading}
                      className="travel-button"
                    >
                      {!hasEnoughTP
                        ? `Need ${tpCost - travelPoints} more TP`
                        : `Travel Here (${tpCost} TP)`}
                    </button>
                  );
                })()}

                {selectedTile.isCurrentLocation && (tileDetails.monsters.length > 0 || selectedTile.bossName) && (
                  <button
                    onClick={handleStartBattle}
                    disabled={selectedCharacterIds.length === 0 || (!selectedTile.bossName && selectedMonsterSpawn === null) || isLoading}
                    className="travel-button"
                    style={{
                      background: selectedCharacterIds.length > 0 && (selectedTile.bossName || selectedMonsterSpawn !== null)
                        ? 'linear-gradient(135deg, #f44336 0%, #c62828 100%)'
                        : undefined
                    }}
                  >
                    {selectedCharacterIds.length === 0
                      ? 'Select Characters to Battle'
                      : !selectedTile.bossName && selectedMonsterSpawn === null
                        ? 'Select a Monster Spawn'
                        : selectedTile.bossName
                          ? `👑 Fight Boss (${selectedCharacterIds.length} heroes)`
                          : `⚔️ Start Battle (${selectedCharacterIds.length} heroes)`
                    }
                  </button>
                )}

                {selectedTile.isCurrentLocation && tileDetails.monsters.length === 0 && !selectedTile.bossName && (
                  <div className="current-location-notice">
                    📍 You are here - No monsters available
                  </div>
                )}

                <button onClick={handleCloseModal} className="cancel-button">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HexWorldMap;
